/**
 * Base Icon component
 * Heavily borrowed from pl_icon
 *
 * @author    Al Ocampo <aocampo@wayfair.com>
 * @copyright 2019 Wayfair LLC - All rights reserved
 */
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {generateTestIds} from '@homebase/common';
import './index.$BRAND.css.js';

function getA11yProps(title) {
  if (title) {
    return {
      role: 'img',
      'aria-label': title,
    };
  }
  return {
    'aria-hidden': true,
  };
}

export default function BaseIcon({
  title,
  className,
  children,
  testIds,
  mode,
  spriteId,
  scalable,
  ...rest
}) {
  // Provide an alterante render mode for use within a local icon sprite
  if (mode === 'define-sprite') {
    return (
      <symbol
        {...rest}
        id={spriteId}
        data-hb-id="pl-icon-symbol"
        {...generateTestIds({testIds})}
      >
        {title && <title>{title}</title>}
        {children}
      </symbol>
    );
  }
  // Provide an alterante render mode for referencing an already defined svg symbol
  if (mode === 'use-sprite') {
    return (
      <svg
        focusable="false"
        {...rest}
        className={cx(
          'pl-BaseIcon BaseIcon',
          {'pl-BaseIcon--scalable': !!scalable},
          className
        )}
        // Not overridable via endpoint, used to enforce consistent accessibility attributes
        // https://www.w3.org/TR/UNDERSTANDING-WCAG20/complete.html#text-equiv-all
        {...getA11yProps(title)}
        // This is technically the entry point for all v2 icons
        data-hb-id="pl-icon-use"
        {...generateTestIds({testIds})}
      >
        <use xlinkHref={'#' + spriteId} />
      </svg>
    );
  }
  // render as an inline svg by default
  return (
    <svg
      focusable="false"
      {...rest}
      className={cx(
        'pl-BaseIcon BaseIcon',
        {'pl-BaseIcon--scalable': !!scalable},
        className
      )}
      // Not overridable via endpoint, used to enforce consistent accessibility attributes
      // https://www.w3.org/TR/UNDERSTANDING-WCAG20/complete.html#text-equiv-all
      {...getA11yProps(title)}
      // This is technically the entry point for all v2 icons
      data-hb-id="pl-icon"
      {...generateTestIds({testIds})}
    >
      {title && <title>{title}</title>}
      {children}
    </svg>
  );
}

BaseIcon.propTypes = {
  // Only takes translated content, no markup
  title: PropTypes.string,
  // A string of classnames to add to the icon for styling
  className: PropTypes.string,
  // Inner markup for each icon
  children: PropTypes.node,
  // Allow alternate render modes for advanced use
  mode: PropTypes.oneOf(['inline-svg', 'define-sprite', 'use-sprite']),
  // Variable line-weights on a per-brand basis (incompatible with deprecated icons)
  scalable: PropTypes.bool,
  // When rendering as or referring to an SVG sprite, a sprite reference id is required
  spriteId(p) {
    if (!p.spriteId && /(define|use)-sprite/.test(p.mode)) {
      return new Error(
        'a `spriteId` is required when using define-sprite or use-sprite render modes'
      );
    }
    return null;
  },
  // Testing identifiers for Cypress, Enzyme, Codeception, and other utilities.
  // Adds `data-[key]-id="[value]"` attributes when set.
  // For more information on test IDs, including testing nested elements, refer
  // to our test IDs documentation.
  testIds: PropTypes.shape({
    // This will be passed to the `data-cypress-id` attribute
    cypress: PropTypes.string,
    // This will be passed to the `data-enzyme-id` attribute
    enzyme: PropTypes.string,
    // This will be passed to the `data-codeception-id` attribute
    codeception: PropTypes.string,
  }),
};

BaseIcon.defaultProps = {
  className: null,
  title: null,
  children: null,
  mode: 'inline-svg',
  scalable: false,
  spriteId: null,
  testIds: {},
};
